import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import {
  Box,
  Container,
  Typography,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  Paper,
  Chip,
  CircularProgress,
  Pagination,
  Stack,
  Avatar,
  Card,
  CardContent,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Email as EmailIcon,
  MarkEmailRead as MarkEmailReadIcon,
  MarkEmailUnread as MarkEmailUnreadIcon,
  Person as PersonIcon,
} from "@mui/icons-material";

import PrimaryTableCell from "app/Components/Tables/PrimaryTableCell";
import StyledTableRow from "app/Components/Tables/StyledTableRow";
import { db } from "config/firebase";
import { doc, updateDoc } from "firebase/firestore";

import { formatDate } from "utils/helperFunctions";
import { getMessages } from "_DatabaseFunctions/cloudCalls/messages";
import { Message } from "types";
import { RootState } from "store";

interface MessagesProps {
  numMsgs: number;
}

function Messages(props: MessagesProps) {
  const firstMsgs = useSelector((state: RootState) => state.messages.messages);
  const user = useSelector((state: RootState) => state.user.user);
  const navigate = useNavigate();
  const [maxPages, setMaxPages] = useState<number>(1);
  const [pageNum, setPageNum] = useState<number>(1);
  const [messages, setMessages] = useState<Message[] | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const nMsgsPerPage = 10;

  useEffect(() => {
    (async function IIFE() {
      if (pageNum === 1) {
        setMessages(firstMsgs);
        setLoading(false);
      } else {
        setLoading(true);
        try {
          const { data } = await getMessages(pageNum, user!.uid);
          setMessages(data);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          console.log(err);
        }
      }
      let maxPage = Math.ceil(props.numMsgs / nMsgsPerPage);
      if (maxPage === 0) {
        maxPage = 1;
      }
      setMaxPages(maxPage);
    })();
  }, [pageNum, firstMsgs]);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    setPageNum(value);
  };

  const handleMessageClick = async (msg: Message) => {
    try {
      // Update the message as read using Firebase v9 syntax
      const messageRef = doc(db, "messages", msg.id);
      await updateDoc(messageRef, {
        read: true,
      });

      // Navigate to the conversation
      navigate(`/conversation/${msg.senderId}`, {
        state: { receiverName: msg.senderName },
      });
    } catch (error) {
      console.error("Error updating message read status:", error);
      // Still navigate even if the update fails
      navigate(`/conversation/${msg.senderId}`, {
        state: { receiverName: msg.senderName },
      });
    }
  };

  const getMessagePreview = (
    content: string,
    maxLength: number = 85,
  ): string => {
    let preview = content.replace(/(<([^>]+)>)/gi, "");
    if (preview.length > maxLength) {
      preview = `${preview.slice(0, maxLength)}...`;
    }
    return preview;
  };

  return (
    <Box sx={{ py: { xs: 2, md: 4 } }}>
      {/* Header Section */}
      <Box sx={{ mb: 4, textAlign: "center" }}>
        <Typography
          variant="h3"
          component="h1"
          gutterBottom
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            color: "primary.main",
            fontWeight: 600,
          }}
        >
          <EmailIcon color="primary" fontSize="inherit" />
          Messages
        </Typography>
        <Typography
          variant="body1"
          color="textSecondary"
          sx={{ maxWidth: 600, mx: "auto", mb: 2 }}
        >
          View and manage your private messages
        </Typography>

        {props.numMsgs > 0 && (
          <Chip
            label={`${props.numMsgs} total messages`}
            color="primary"
            variant="outlined"
          />
        )}
      </Box>

      <Container maxWidth="lg">
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={60} />
          </Box>
        ) : (
          <>
            {!messages || messages.length === 0 ? (
              <Card sx={{ textAlign: "center", py: 6 }}>
                <CardContent>
                  <EmailIcon sx={{ fontSize: 64, color: "grey.400", mb: 2 }} />
                  <Typography variant="h5" color="textSecondary" gutterBottom>
                    No messages found
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    When you receive messages, they'll appear here.
                  </Typography>
                </CardContent>
              </Card>
            ) : (
              <>
                <TableContainer 
                  component={Paper} 
                  elevation={10}
                  sx={{ borderRadius: "20px", mb: 4 }}
                >
                  <Table className="tablelist">
                    <TableHead>
                      <StyledTableRow>
                        <PrimaryTableCell>From</PrimaryTableCell>
                        <PrimaryTableCell>Message</PrimaryTableCell>
                        <PrimaryTableCell align="right">Date</PrimaryTableCell>
                        <PrimaryTableCell align="center">Status</PrimaryTableCell>
                      </StyledTableRow>
                    </TableHead>
                    <TableBody>
                      {messages.map((msg) => (
                        <StyledTableRow
                          key={msg.id}
                          hover
                          onClick={() => handleMessageClick(msg)}
                          sx={{
                            cursor: "pointer",
                            "&:hover": {
                              backgroundColor: "action.hover",
                            },
                            ...(msg.read
                              ? {}
                              : { backgroundColor: "action.selected" }),
                          }}
                        >
                          <PrimaryTableCell>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                              }}
                            >
                              <Avatar sx={{ width: 32, height: 32 }}>
                                <PersonIcon />
                              </Avatar>
                              <Typography
                                variant="body2"
                                fontWeight={msg.read ? "normal" : "bold"}
                              >
                                {msg.senderName}
                              </Typography>
                            </Box>
                          </PrimaryTableCell>

                          <PrimaryTableCell>
                            <Typography
                              variant="body2"
                              fontWeight={msg.read ? "normal" : "bold"}
                              sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                maxWidth: { xs: 200, sm: 400, md: 600 },
                              }}
                            >
                              {getMessagePreview(msg.content)}
                            </Typography>
                          </PrimaryTableCell>

                          <PrimaryTableCell align="right">
                            <Typography variant="body2" color="textSecondary">
                              {formatDate(msg.timeStamp)}
                            </Typography>
                          </PrimaryTableCell>

                          <PrimaryTableCell align="center">
                            <Tooltip title={msg.read ? "Read" : "Unread"}>
                              <IconButton
                                size="small"
                                color={msg.read ? "default" : "primary"}
                              >
                                {msg.read ? (
                                  <MarkEmailReadIcon />
                                ) : (
                                  <MarkEmailUnreadIcon />
                                )}
                              </IconButton>
                            </Tooltip>
                          </PrimaryTableCell>
                        </StyledTableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {maxPages > 1 && (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", mt: 4 }}
                  >
                    <Stack spacing={2} alignItems="center">
                      <Typography variant="body2" color="textSecondary">
                        Page {pageNum} of {maxPages}
                      </Typography>
                      <Pagination
                        count={maxPages}
                        page={pageNum}
                        onChange={handlePageChange}
                        color="primary"
                        size="large"
                        showFirstButton
                        showLastButton
                      />
                    </Stack>
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </Container>
    </Box>
  );
}

export default Messages;
